import React, { useState, useMemo } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useEventWorkouts } from '../../hooks/useWearables';
import {
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Button,
  Badge,
  Skeleton,
  Select,
  TextInput,
  Alert,
} from '@mantine/core';
import { IconActivity, IconRefresh, IconClock, IconFlame, IconHeart } from '@tabler/icons-react';

function formatDuration(seconds) {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

function getDateRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days === 'all' ? 12000 : Number(days)));
  return { start_date: start.toISOString().split('T')[0], end_date: end.toISOString().split('T')[0] };
}

function Workouts() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [rangeDays, setRangeDays] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [cursor, setCursor] = useState(null);

  const selectedRange = rangeDays === 'custom'
    ? (
      customStart
        ? { start_date: customStart, end_date: customEnd || new Date().toISOString().split('T')[0] }
        : getDateRange(7)
    )
    : getDateRange(rangeDays);

  const { workouts, pagination, loading, error, refresh } = useEventWorkouts({
    limit: 50,
    startDate: selectedRange.start_date,
    endDate: selectedRange.end_date,
    cursor,
  });

  const stats = useMemo(() => {
    if (workouts.length === 0) return null;
    const totalDuration = workouts.reduce((s, w) => s + (w.duration_seconds || 0), 0);
    const totalCalories = workouts.reduce((s, w) => s + (w.calories_kcal || 0), 0);
    const totalDistance = workouts.reduce((s, w) => s + (w.distance_meters || 0), 0);
    const withHr = workouts.filter((w) => w.avg_heart_rate_bpm);
    const avgHr = withHr.length > 0 ? Math.round(withHr.reduce((s, w) => s + (w.avg_heart_rate_bpm || 0), 0) / withHr.length) : null;
    return { count: workouts.length, totalDuration, totalCalories: Math.round(totalCalories), totalDistance, avgHr };
  }, [workouts]);

  const buildFilters = (nextCursor = null) => ({
    ...selectedRange,
    limit: 50,
    cursor: nextCursor,
  });

  const handleRefresh = () => {
    setCursor(null);
    refresh(buildFilters(null));
  };

  const handleRangeChange = (value) => {
    const nextRange = value || '7';
    setCursor(null);
    setRangeDays(nextRange);
    if (nextRange !== 'custom') {
      refresh({ ...getDateRange(nextRange), limit: 50, cursor: null });
    }
  };

  if (authLoading) return (<Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400}><Skeleton height={400} /></Container>);
  if (!isAuthenticated) return (<Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400}><Card withBorder><Title order={2}>Workouts</Title><Text c="dimmed" mt="xs">Please log in.</Text></Card></Container>);

  return (
    <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
      <Stack gap="md">
        <Title order={1} size="h2" style={{ marginTop: 0 }}>Workouts</Title>
        {error && <Alert color="red" variant="light">{error}</Alert>}

        <Card withBorder radius="md" p="md">
          <Group justify="space-between" mb="md">
            <Text fw={600}>Filters</Text>
            <Group>
              <Select data={RANGE_OPTIONS} value={rangeDays} onChange={handleRangeChange} w={130} />
              <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={handleRefresh} disabled={loading}>Refresh</Button>
            </Group>
          </Group>
          {rangeDays === 'custom' && (
            <Group mt="sm">
              <TextInput
                label="Start date"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <TextInput
                label="End date"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
              <Button
                mt={24}
                variant="light"
                size="sm"
                onClick={handleRefresh}
                disabled={!customStart || loading}
              >
                Load Data
              </Button>
            </Group>
          )}
          <Text size="xs" c="dimmed" mt="sm">Showing up to 50 workouts per page.</Text>
        </Card>

        {stats && (
          <Card withBorder radius="md" p="md">
            <Text fw={600} mb="md">Summary</Text>
            <Grid>
              <Grid.Col span={{ base: 6, sm: 2.4 }}><div style={{ textAlign: 'center' }}><Text size="xl" fw={700} c="pink">{stats.count}</Text><Text size="xs" c="dimmed">Workouts</Text></div></Grid.Col>
              <Grid.Col span={{ base: 6, sm: 2.4 }}><div style={{ textAlign: 'center' }}><Text size="xl" fw={700} c="blue">{formatDuration(stats.totalDuration)}</Text><Text size="xs" c="dimmed">Total Time</Text></div></Grid.Col>
              <Grid.Col span={{ base: 6, sm: 2.4 }}><div style={{ textAlign: 'center' }}><Text size="xl" fw={700} c="orange">{stats.totalCalories.toLocaleString()}</Text><Text size="xs" c="dimmed">Calories</Text></div></Grid.Col>
              <Grid.Col span={{ base: 6, sm: 2.4 }}><div style={{ textAlign: 'center' }}><Text size="xl" fw={700} c="green">{(stats.totalDistance / 1000).toFixed(1)} km</Text><Text size="xs" c="dimmed">Distance</Text></div></Grid.Col>
              <Grid.Col span={{ base: 6, sm: 2.4 }}><div style={{ textAlign: 'center' }}><Text size="xl" fw={700} c="red">{stats.avgHr ?? '-'}</Text><Text size="xs" c="dimmed">Avg HR</Text></div></Grid.Col>
            </Grid>
          </Card>
        )}

        <Card withBorder radius="md" p="md">
          <Group mb="md"><IconActivity size={20} color="var(--mantine-color-pink-6)" /><Text fw={600}>Recent Workouts</Text></Group>
          {loading ? (
            <Stack gap="xs">{[1,2,3,4,5].map((i) => <Skeleton key={i} height={80} />)}</Stack>
          ) : workouts.length > 0 ? (
            <Stack gap="md">
              {workouts.map((workout) => {
                const startDate = new Date(workout.start_datetime || workout.start_time);
                return (
                  <Card key={workout.id || workout.start_time} withBorder radius="md" p="md">
                    <Group justify="space-between" mb="xs">
                      <div>
                        <Text fw={600}>{workout.type?.replace(/_/g, ' ') || 'Workout'}</Text>
                        <Text size="xs" c="dimmed">{startDate.toLocaleDateString()} • {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
                      </div>
                      {workout.source_name && <Badge variant="light">{workout.source_name}</Badge>}
                    </Group>
                    <Grid>
                      {workout.duration_seconds && <Grid.Col span={3}><Group gap="xs"><IconClock size={16} /><Text size="sm">{formatDuration(workout.duration_seconds)}</Text></Group></Grid.Col>}
                      {workout.calories_kcal != null && <Grid.Col span={3}><Group gap="xs"><IconFlame size={16} /><Text size="sm">{Math.round(workout.calories_kcal)} kcal</Text></Group></Grid.Col>}
                      {workout.avg_heart_rate_bpm != null && <Grid.Col span={3}><Group gap="xs"><IconHeart size={16} /><Text size="sm">{workout.avg_heart_rate_bpm} bpm</Text></Group></Grid.Col>}
                      {workout.distance_meters != null && <Grid.Col span={3}><Text size="sm">{(workout.distance_meters / 1000).toFixed(2)} km</Text></Grid.Col>}
                    </Grid>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Text c="dimmed" ta="center" py="xl">No workouts yet</Text>
          )}
          {(pagination?.previous_cursor || pagination?.has_more) && (
            <Group justify="space-between" mt="md">
              <Button
                variant="light"
                disabled={!pagination?.previous_cursor || loading}
                onClick={() => {
                  setCursor(pagination.previous_cursor);
                  refresh(buildFilters(pagination.previous_cursor));
                }}
              >
                Previous Page
              </Button>
              <Text size="sm" c="dimmed">
                {pagination?.total_count != null ? `${pagination.total_count} total` : 'Paginated results'}
              </Text>
              <Button
                variant="light"
                disabled={!pagination?.has_more || loading}
                onClick={() => {
                  setCursor(pagination.next_cursor);
                  refresh(buildFilters(pagination.next_cursor));
                }}
              >
                Next Page
              </Button>
            </Group>
          )}
        </Card>
      </Stack>
    </Container>
  );
}

export default Workouts;
